import { useContext } from 'react';
import { useAppParams } from '@/navigation';

import { SectionApi } from '@/api';
import { useAppDispatch } from '@/hooks';
import { updateFieldFailedAlert } from '@/services/alert';
import { getStoreState } from '@/store';
import { FormContext, SaveKeyContext } from '@/forms/contexts';
import { peekSaveQueue, releaseSetAside, setAside } from '@/forms/save-queue';
import { clearBlockedEditsFor, selectBlockedEdits } from '@/forms/store';
import { FormDomain } from '@/forms/typings';
import type { FormField } from '@/forms/typings';
import { getBaseFieldId } from '@/forms/utils';
import { forEachFormField } from '@/forms/utils/formFieldChildKeys';
import type { GroupActionOutcome } from '@/store/thunks/groupActionOutcome';
import {
  addAttestationGroup,
  addMissingItemFormGroup,
  addSectionGroup,
  clearAttestationGroup,
  clearMissingItemFormGroup,
  clearSectionGroup,
  fetchMissingItemForm,
  fetchSection,
  fetchSectionSummary,
  removeAttestationGroup,
  removeMissingItemFormGroup,
  removeSectionGroup,
  updateMissingItemFormConditionalFieldsGroup,
  updateMissingItemFormStagedFieldsGroup,
  updateSectionConditionalFieldsGroup,
  updateStagedFieldsGroup,
} from '@/store/thunks';
import type {
  UpdateSectionConditionalFieldsGroupRequestDTO,
  UpdateStagedFieldsGroupRequestDTO,
} from '@/api/section/DTOs';

/** How many entries the repeating group of `groupId` has in `fields`. */
const countEntries = (fields: readonly FormField[], groupId: string) => {
  const baseId = getBaseFieldId(groupId);
  const entryIds = new Set<string>();
  forEachFormField(fields, field => {
    if (field.fieldId !== baseId && getBaseFieldId(field.fieldId) === baseId) {
      entryIds.add(field.fieldId);
    }
  });
  return entryIds.size;
};

/**
 * Returns add/remove/clear/update group callbacks bound to the current FormDomain.
 * Presenters call these without knowing which domain they live in — the hook picks
 * the right thunk and reads the right URL params (applicationId+sectionId for AppForm,
 * missingItemId for MPI). Remove and clear also lock the form and hold back the queued
 * values of the entries they renumber or blank.
 */
export const useGroupActions = () => {
  const dispatch = useAppDispatch();
  const { domain, whileInert, formFieldGroups } = useContext(FormContext);
  const saveKey = useContext(SaveKeyContext);
  const { applicationId, sectionId, missingItemId } = useAppParams();

  const appId = Number(applicationId);
  const sectId = Number(sectionId);
  const mpiId = Number(missingItemId);
  const isMPI = domain === FormDomain.MissingItemForm;
  const isAppForm = domain === FormDomain.ApplicationForm;

  // Each action refetches the form, which would remount it on values from before
  // the edits still queued. With nothing queued it dispatches in the same tick.
  const afterPendingSaves = <T>(action: () => T): T | Promise<T> => {
    const queue = saveKey === undefined ? undefined : peekSaveQueue(saveKey);
    if (!queue || queue.getSnapshot().pendingFieldIds.length === 0) {
      return action();
    }
    return queue.flush().then(action);
  };

  // Entry ids carry the entry's position: removing entry k renumbers every later entry and
  // clearing it blanks k in place, so a value held there would land on the wrong one. Earlier
  // entries keep their ids, and with them their held values.
  const takeHeldValuesOfEntries = (
    groupId: string,
    { laterEntriesToo }: { laterEntriesToo: boolean },
  ): { putBack: () => void; drop: () => void } => {
    if (saveKey === undefined) return { putBack: () => {}, drop: () => {} };
    const entryPrefix = `${getBaseFieldId(groupId)}_$`;
    // An id without a position is taken as the first entry.
    const target = Number(/_\$(\d+)$/.exec(groupId)?.[1] ?? 0);
    const affected = (fieldId: string) => {
      if (!fieldId.startsWith(entryPrefix)) return false;
      const entry = /^(\d+)(?:_|$)/.exec(fieldId.slice(entryPrefix.length));
      if (!entry) return false;
      const index = Number(entry[1]);
      return laterEntriesToo ? index >= target : index === target;
    };

    const queue = peekSaveQueue(saveKey);
    const taken = queue?.discard(queue.getSnapshot().pendingFieldIds.filter(affected));
    // Out of the queue they are neither pending nor blocked, so a snapshot landing meanwhile
    // would reset them on screen; set aside, they stay the provider's until the action settles.
    const takenIds = Object.keys(taken?.values ?? {});
    if (taken) setAside(saveKey, taken.values);

    return {
      putBack: () => {
        releaseSetAside(saveKey, takenIds);
        if (taken) queue?.restore(taken.values, taken.status);
      },
      drop: () => {
        releaseSetAside(saveKey, takenIds);
        const blocked = Object.keys(selectBlockedEdits(saveKey)(getStoreState()));
        dispatch(clearBlockedEditsFor({ saveKey, fieldIds: blocked.filter(affected) }));
      },
    };
  };

  // Waiting values are taken before the dispatch: Retry, a tab hide and the unload flush all
  // reach the queue past the inert form. They go back unless the server took the action, since
  // a refused one moved nothing. Blocked edits are never sent, so they stay, keeping the leave
  // guards up, until the server takes it. The form refetched after a lost answer settles it.
  const withHeldValuesTaken = async <T>(
    groupId: string,
    scope: { laterEntriesToo: boolean },
    tookEffect: GroupActionOutcome['tookEffect'],
    act: (outcome: GroupActionOutcome) => T | Promise<T>,
  ): Promise<T> => {
    const { putBack, drop } = takeHeldValuesOfEntries(groupId, scope);
    let settled = false;
    try {
      return await act({
        onAccepted: () => {
          settled = true;
          drop();
        },
        onNotTaken: () => {
          settled = true;
          putBack();
        },
        tookEffect,
      });
    } finally {
      if (!settled) putBack();
    }
  };

  const addGroup = (groupId: string) =>
    afterPendingSaves(() => {
      if (isMPI) return dispatch(addMissingItemFormGroup(mpiId, groupId));
      if (isAppForm) return dispatch(addSectionGroup(appId, sectId, groupId));
      return dispatch(addAttestationGroup(appId, groupId));
    });

  // An edit made while a remove or clear runs would be held under an id the server is about to
  // renumber or blank, so the form takes none until the refetch replaces it or the action fails.
  const inertWhile = <T>(task: () => T | Promise<T>) =>
    whileInert ? whileInert(task) : task();

  // A changed entry count means later ids moved. With no form to tell, the remove counts as
  // taken, so nothing held is ever sent under an id the server may have renumbered.
  const removeGroup = (groupId: string) => {
    const entries = countEntries(formFieldGroups, groupId);
    const tookEffect = (fields: FormField[] | undefined) =>
      fields === undefined || countEntries(fields, groupId) !== entries;

    return inertWhile(() =>
      afterPendingSaves(() =>
        withHeldValuesTaken(groupId, { laterEntriesToo: true }, tookEffect, outcome => {
          if (isMPI)
            return dispatch(removeMissingItemFormGroup(mpiId, groupId, outcome));
          if (isAppForm)
            return dispatch(removeSectionGroup(appId, sectId, groupId, outcome));
          return dispatch(removeAttestationGroup(appId, groupId, outcome));
        }),
      ),
    );
  };

  // A lost clear answer counts as taken: a server clear keeps the entry's Not Applicable ticks,
  // so no refetch tells it apart from one never made. The entry keeps its id, so dropping what it
  // held never misplaces anything, and is what the provider asked for.
  const clearTookEffect = () => true;

  const clearGroup = (groupId: string) =>
    inertWhile(() =>
      afterPendingSaves(() =>
        withHeldValuesTaken(
          groupId,
          { laterEntriesToo: false },
          clearTookEffect,
          outcome => {
            if (isMPI)
              return dispatch(clearMissingItemFormGroup(mpiId, groupId, outcome));
            if (isAppForm)
              return dispatch(clearSectionGroup(appId, sectId, groupId, outcome));
            return dispatch(clearAttestationGroup(appId, groupId, outcome));
          },
        ),
      ),
    );

  // Asked once the queued saves land, so the server judges the answers on screen.
  /** The answers removing (or, when `clears`, clearing) `groupId` would clear; undefined when the server could not say. */
  const getRemovalImpact = (
    groupId: string,
    clears: boolean,
  ): Promise<string[] | undefined> =>
    Promise.resolve(
      afterPendingSaves(async () => {
        if (!isAppForm) return [];

        const result = await new SectionApi().getGroupRemovalImpact(
          appId,
          sectId,
          groupId,
          clears,
        );
        if (!result.success || !result.data) {
          updateFieldFailedAlert();
          return undefined;
        }
        return result.data.affectedAnswers;
      }),
    );

  // Staged Continue refetches in the foreground right after this lands.
  const updateStagedGroup = (
    formField: UpdateStagedFieldsGroupRequestDTO['formField'],
  ) =>
    afterPendingSaves(() => {
      if (isMPI)
        return dispatch(
          updateMissingItemFormStagedFieldsGroup(mpiId, formField),
        );
      return dispatch(updateStagedFieldsGroup(appId, sectId, formField));
    });

  const updateConditionalGroup = (
    formField: UpdateSectionConditionalFieldsGroupRequestDTO['formField'],
  ) => {
    if (isMPI)
      return dispatch(
        updateMissingItemFormConditionalFieldsGroup(mpiId, formField),
      );
    return dispatch(updateSectionConditionalFieldsGroup(appId, sectId, formField));
  };

  /** Re-fetches the current form (and section summary on AppForm). Per-domain. */
  const refreshForm = () => {
    if (isMPI) {
      dispatch(fetchMissingItemForm(mpiId));
      return;
    }
    if (isAppForm) {
      dispatch(fetchSection(appId, sectId));
      dispatch(fetchSectionSummary(appId, sectId));
    }
  };

  return {
    addGroup,
    removeGroup,
    clearGroup,
    getRemovalImpact,
    updateStagedGroup,
    updateConditionalGroup,
    refreshForm,
  };
};
